import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button, YStack } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import TextArea from '@/components/ui/TextArea';

import { useSaveProactiveAgentVariantMutation } from '../proactive-agent.query';

import type { ProactiveAgentVariant } from '@littlebird/sdk';

type ProactiveAgentCreateDialogProps = {
  configurations: Record<string, ProactiveAgentVariant>;
  startableAgents: string[];
  defaultAgent: string;
  onClose: () => void;
};

export const ProactiveAgentCreateDialog = ({
  configurations,
  startableAgents,
  defaultAgent,
  onClose,
}: ProactiveAgentCreateDialogProps) => {
  const [copiedAgent, setCopiedAgent] = useState(defaultAgent);
  const [initialPrompt, setInitialPrompt] = useState(
    configurations[defaultAgent].initial_prompt
  );
  const [promptName, setPromptName] = useState('');
  const save = useSaveProactiveAgentVariantMutation();

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !save.isPending) onClose();
      }}
    >
      <Dialog.Content className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <Dialog.Header>
          <Dialog.Title>Create agent</Dialog.Title>
          <Dialog.Description>
            Start from an existing agent and edit its initial prompt. Saving
            adds a new agent to the list. It does not start the new agent or
            change agents that are already running.
          </Dialog.Description>
        </Dialog.Header>
        <YStack className="gap-4">
          <YStack className="gap-2">
            <label id="proactive-base-label" className="text-sm font-medium">
              Base agent
            </label>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                aria-labelledby="proactive-base-label"
                className="w-full justify-between"
                disabled={save.isPending}
              >
                <span className="truncate">{copiedAgent}</span>
                <Icons.chevronDown className="size-3 shrink-0" />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="start" className="max-h-72">
                <DropdownMenu.RadioGroup
                  value={copiedAgent}
                  onValueChange={(value) => {
                    setCopiedAgent(value);
                    setInitialPrompt(configurations[value].initial_prompt);
                    setPromptName('');
                  }}
                >
                  {startableAgents.map((name) => (
                    <DropdownMenu.RadioItem key={name} value={name}>
                      {name}
                    </DropdownMenu.RadioItem>
                  ))}
                </DropdownMenu.RadioGroup>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </YStack>
          <Input
            label="Prompt name"
            placeholder="For example, Verified Sources"
            info="Added to the base agent name. Leave blank for a generated name."
            value={promptName}
            maxLength={100}
            disabled={save.isPending}
            onChange={(event) => setPromptName(event.target.value)}
          />
          <YStack className="gap-2">
            <label
              htmlFor="proactive-initial-prompt"
              className="text-sm font-medium"
            >
              Initial prompt
            </label>
            <TextArea
              id="proactive-initial-prompt"
              className="min-h-[20rem] resize-y text-sm"
              value={initialPrompt}
              maxLength={100000}
              disabled={save.isPending}
              onChange={(event) => setInitialPrompt(event.target.value)}
            />
          </YStack>
        </YStack>
        <Dialog.Footer>
          <Button
            variant="secondary"
            disabled={save.isPending}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            disabled={save.isPending || !initialPrompt.trim()}
            onClick={() =>
              save.mutate(
                {
                  baseAgent: configurations[copiedAgent].base_agent,
                  promptName,
                  initialPrompt,
                },
                { onSuccess: onClose }
              )
            }
          >
            {save.isPending ? 'Saving...' : 'Save agent'}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
